import React, { useEffect, useRef, useState } from "react";
import * as S from "./styles";
import SignaturePad from "./SignaturePad";
import {
  FiCheckCircle,
  FiSend,
  FiFileText,
  FiAlertTriangle,
} from "react-icons/fi";

const API_BASE = process.env.REACT_APP_API || "https://api.vidal-app.com";

const INITIAL_FORM = {
  // Seção I
  q1_continua: "",
  // Seção 1 — Identificação
  q2_razaoSocial: "",
  q3_cnpj: "",
  q4_codigo: "",
  q5_representante: "",
  q6_cargo: "",
  q7_cpf: "",
  q8_contato: "",
  q9_contatoFiscal: "",
  q10_negocio: "",
  q11_banco: "",
  q11_agencia: "",
  q11_conta: "",
  q11_pix: "",
  // Seção 2 — Regime tributário
  q12_regime: "",
  q12_outro: "",
  q13_regimeIbsCbs: "",
  q14_cienteHibrido: "",
  q15_regimesEspeciais: "",
  q15_descricao: "",
  q16_regularidade: "",
  q16_motivo: "",
  // Seção 3 — Emissão de NF
  q17_emiteNf: "",
  q17_previsao: "",
  q18_prazoAdaptacao: "",
  // Seção 4 — Fornecimento
  q19_fornecedoresAlternativos: "",
  q20_prazoEntrega: "",
  q20_capacidadeDemanda: "",
  // Seção 5 — Condições
  q21_aceite: false,
  // Seção 6
  q22_observacoes: "",
  // Seção VII — Assinatura
  localData: "",
  assinaturaNome: "",
  assinaturaCpf: "",
  assinaturaCargo: "",
};

const digits = (v) => String(v || "").replace(/\D/g, "");

const maskCnpj = (v) => {
  const d = digits(v).slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const maskCpf = (v) => {
  const d = digits(v).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
};

const Radio = ({ name, value, checked, onChange, children }) => (
  <S.RadioOption $checked={checked}>
    <span className="box">{checked ? "X" : ""}</span>
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
    />
    <span>{children}</span>
  </S.RadioOption>
);

export default function AutomecQuestionario() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [config, setConfig] = useState({
    deadline: "[DATA]",
    emailContato: "[E-MAIL]",
    emailCadastro: "[E-MAIL DE CADASTRO]",
    emailFiscal: "[E-MAIL FISCAL DA AUTOMEC]",
  });
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/automec/config`)
      .then((r) => r.json())
      .then((c) => setConfig((prev) => ({ ...prev, ...c })))
      .catch(() => {});
  }, []);

  const set = (field) => (value) => {
    const v = value?.target ? value.target.value : value;
    setForm((f) => {
      const next = { ...f, [field]: v };
      // Mudança de regime limpa respostas condicionais que não se aplicam mais
      if (field === "q12_regime" && !["simples", "mei"].includes(v)) {
        next.q13_regimeIbsCbs = "";
        next.q14_cienteHibrido = "";
      }
      if (field === "q12_regime" && v !== "outro") next.q12_outro = "";
      if (field === "q15_regimesEspeciais" && v === "nao") next.q15_descricao = "";
      if (field === "q16_regularidade" && v === "sim") next.q16_motivo = "";
      if (field === "q17_emiteNf" && v === "sim") next.q17_previsao = "";
      return next;
    });
  };

  const continua = form.q1_continua === "sim";
  const naoContinua = form.q1_continua === "nao";
  const isSimplesMei = ["simples", "mei"].includes(form.q12_regime);

  const validate = () => {
    const e = [];
    if (!form.q1_continua) e.push("1. Informe se pretende continuar fornecendo");
    if (!form.q2_razaoSocial.trim()) e.push("2. Razão social é obrigatória");
    if (digits(form.q3_cnpj).length !== 14) e.push("3. CNPJ inválido");
    if (!form.q5_representante.trim()) e.push("5. Representante legal é obrigatório");
    if (digits(form.q7_cpf).length !== 11) e.push("7. CPF inválido");
    if (!form.q8_contato.trim()) e.push("8. E-mail/telefone de contato é obrigatório");

    if (continua) {
      if (!form.q12_regime) e.push("12. Informe o regime tributário");
      if (form.q12_regime === "outro" && !form.q12_outro.trim()) e.push("12. Descreva o regime");
      if (isSimplesMei && !form.q13_regimeIbsCbs) e.push("13. Resposta obrigatória para Simples/MEI");
      if (isSimplesMei && !form.q14_cienteHibrido) e.push("14. Resposta obrigatória para Simples/MEI");
      if (!form.q15_regimesEspeciais) e.push("15. Resposta obrigatória");
      if (form.q15_regimesEspeciais === "sim" && !form.q15_descricao.trim()) e.push("15. Descreva os benefícios");
      if (!form.q16_regularidade) e.push("16. Resposta obrigatória");
      if (["parcial", "nao"].includes(form.q16_regularidade) && !form.q16_motivo.trim()) e.push("16. Informe o motivo");
      if (!form.q17_emiteNf) e.push("17. Resposta obrigatória");
      if (form.q17_emiteNf === "nao" && !form.q17_previsao.trim()) e.push("17. Informe a previsão");
      if (!form.q19_fornecedoresAlternativos) e.push("19. Resposta obrigatória");
      if (!form.q20_capacidadeDemanda) e.push("20. Informe a capacidade de atendimento");
      if (!form.q21_aceite) e.push("21. É obrigatório aceitar as condições");
    }

    if (!form.assinaturaNome.trim()) e.push("Assinatura: nome do representante é obrigatório");
    if (digits(form.assinaturaCpf).length !== 11) e.push("Assinatura: CPF inválido");
    if (!form.assinaturaCargo.trim()) e.push("Assinatura: cargo é obrigatório");
    if (!hasSignature || sigRef.current?.isEmpty()) e.push("Assinatura desenhada é obrigatória");

    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      return;
    }
    setErrors([]);
    setSubmitting(true);

    try {
      const resp = await fetch(`${API_BASE}/automec/respostas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          respostas: form,
          assinatura: sigRef.current.toDataURL(),
        }),
      });

      const data = await resp.json().catch(() => ({}));

      if (resp.status === 409) {
        setErrors([data.error || "Este CNPJ já enviou uma resposta.", data.message].filter(Boolean));
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        return;
      }

      if (!resp.ok) {
        setErrors([data.error || "Erro ao enviar resposta", ...(data.details || [])].flat());
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        return;
      }

      setDone(data.protocolo);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setErrors(["Falha de conexão. Verifique sua internet e tente novamente."]);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <S.Page>
        <S.GlobalStyle />
        <S.TopBar>
          <div className="inner">
            <div className="group">GRUPO AUTOMEC</div>
            <h1>Questionário e Declaração de Fornecedor</h1>
          </div>
        </S.TopBar>
        <S.SuccessWrap>
          <div className="icon"><FiCheckCircle /></div>
          <h2>Resposta registrada com sucesso</h2>
          <p>
            Obrigado por responder ao questionário. Suas informações foram enviadas
            ao Departamento de Compras do Grupo Automec.
          </p>
          <p>Guarde o número de protocolo abaixo para referência futura.</p>
          <div className="protocol">Protocolo: {done}</div>
        </S.SuccessWrap>
        <S.Footer>Grupo Automec · Departamento de Compras</S.Footer>
      </S.Page>
    );
  }

  return (
    <S.Page>
      <S.GlobalStyle />
      <S.TopBar>
        <div className="inner">
          <div className="group">GRUPO AUTOMEC</div>
          <h1>Questionário e Declaração de Fornecedor</h1>
          <div className="sub">
            Reforma Tributária sobre o Consumo — IBS e CBS · Válido para todos os
            fins de direito · Obrigatório para fornecer à Automec a partir de 2027
          </div>
          <div className="tags">
            <span>EC 132/2023</span>
            <span>LC 214/2025</span>
            <span>Vigência 2027</span>
          </div>
        </div>
      </S.TopBar>

      <S.Container>
        <form onSubmit={handleSubmit} noValidate>
          {/* ─── INTRO ─── */}
          <S.IntroCard>
            <p><strong>Prezado fornecedor,</strong></p>
            <p>
              Com a implementação da Reforma Tributária (EC 132/2023 e LC 214/2025),
              a apuração de tributos e o aproveitamento de créditos mudam a partir
              de 2027. Este documento prepara e formaliza as nossas relações
              comerciais diante das novas regras.
            </p>
            <p>
              O preenchimento é <strong>obrigatório para as empresas que pretendam
              fornecer à Automec a partir de 2027</strong>. As observações e
              obrigações aqui constantes farão lei entre as partes.
            </p>
            <p>
              <strong>Parceria:</strong> a Automec valoriza relações de longo prazo
              e dará preferência, em suas compras, aos fornecedores que
              demonstrarem conformidade e colaboração na adequação às novas regras.
            </p>
            <p>
              <strong>Instruções:</strong> marque a opção aplicável em cada questão,
              preencha os campos abertos e assine ao final. Responder até{" "}
              <strong>{config.deadline}</strong>. Dúvidas: {config.emailContato}.
            </p>
          </S.IntroCard>

          {/* ─── SEÇÃO I ─── */}
          <S.Card>
            <S.SectionTitle>Seção I · Continuidade e Atualização Cadastral</S.SectionTitle>
            <S.Question>
              <div className="q-label">
                <span className="q-num">1.</span>
                <span>A empresa pretende continuar fornecendo à Automec em 2027 e nos exercícios seguintes?</span>
              </div>
              <S.RadioGroup>
                <Radio name="q1" value="sim" checked={form.q1_continua === "sim"} onChange={set("q1_continua")}>Sim</Radio>
                <Radio name="q1" value="nao" checked={form.q1_continua === "nao"} onChange={set("q1_continua")}>Não</Radio>
              </S.RadioGroup>
              {continua && (
                <p className="q-note" style={{ marginTop: 10 }}>
                  Em caso afirmativo, responda integralmente este documento e atente-se às
                  observações da Automec, que farão lei entre as partes.
                </p>
              )}
              {naoContinua && (
                <p className="q-note" style={{ marginTop: 10 }}>
                  Agradecemos a parceria. Preencha a identificação e assine ao final
                  para formalizar a descontinuidade.
                </p>
              )}
            </S.Question>

            <S.LegalBox>
              <div className="lb-title">Observação da Automec — Faz lei entre as partes</div>
              <p>
                A empresa obriga-se a comunicar imediatamente, pelo e-mail{" "}
                {config.emailCadastro}, qualquer alteração de quadro societário
                (inclusão ou saída de sócios), venda, encerramento, mudança de
                endereço, alteração do comprador/contato na Automec ou quaisquer
                outras alterações cadastrais.
              </p>
            </S.LegalBox>
          </S.Card>

          {/* ─── SEÇÃO 1 — IDENTIFICAÇÃO ─── */}
          <S.Card>
            <S.SectionTitle>Seção 1 · Identificação do Fornecedor</S.SectionTitle>

            <S.Question>
              <div className="q-label"><span className="q-num">2.</span><span>Razão social</span></div>
              <S.Input value={form.q2_razaoSocial} onChange={set("q2_razaoSocial")} placeholder="Razão social completa" />
            </S.Question>

            <S.Grid2>
              <S.Question>
                <div className="q-label"><span className="q-num">3.</span><span>CNPJ</span></div>
                <S.Input value={form.q3_cnpj} onChange={(e) => set("q3_cnpj")(maskCnpj(e.target.value))} placeholder="00.000.000/0000-00" inputMode="numeric" />
              </S.Question>
              <S.Question>
                <div className="q-label"><span className="q-num">4.</span><span>Código do fornecedor — uso interno Automec <em style={{ fontWeight: 400, color: "#94a3b8" }}>(opcional)</em></span></div>
                <S.Input value={form.q4_codigo} onChange={set("q4_codigo")} placeholder="Se souber" />
              </S.Question>
            </S.Grid2>

            <S.Grid2>
              <S.Question>
                <div className="q-label"><span className="q-num">5.</span><span>Nome do representante legal (respondente)</span></div>
                <S.Input value={form.q5_representante} onChange={set("q5_representante")} placeholder="Nome completo" />
              </S.Question>
              <S.Question>
                <div className="q-label"><span className="q-num">6.</span><span>Cargo / função</span></div>
                <S.Input value={form.q6_cargo} onChange={set("q6_cargo")} placeholder="Ex.: Diretor, Sócio-proprietário" />
              </S.Question>
            </S.Grid2>

            <S.Grid2>
              <S.Question>
                <div className="q-label"><span className="q-num">7.</span><span>CPF do representante legal</span></div>
                <S.Input value={form.q7_cpf} onChange={(e) => set("q7_cpf")(maskCpf(e.target.value))} placeholder="000.000.000-00" inputMode="numeric" />
              </S.Question>
              <S.Question>
                <div className="q-label"><span className="q-num">8.</span><span>E-mail e telefone de contato</span></div>
                <S.Input value={form.q8_contato} onChange={set("q8_contato")} placeholder="email@empresa.com.br · (00) 00000-0000" />
              </S.Question>
            </S.Grid2>

            <S.Question>
              <div className="q-label"><span className="q-num">9.</span><span>Contato da área fiscal / tributária (nome e e-mail)</span></div>
              <S.Input value={form.q9_contatoFiscal} onChange={set("q9_contatoFiscal")} placeholder="Nome · email fiscal" />
            </S.Question>
          </S.Card>

          {continua && (
            <>
              {/* ─── SEÇÃO 1b — NEGÓCIO + BANCO ─── */}
              <S.Card>
                <S.Question style={{ marginTop: 0 }}>
                  <div className="q-label">
                    <span className="q-num">10.</span>
                    <span>O que vende para a Automec? Com quem negocia na Automec (comprador)? O que gostaria de vender, além do que já vende?</span>
                  </div>
                  <S.TextArea value={form.q10_negocio} onChange={set("q10_negocio")} placeholder="Descreva produtos/serviços, comprador e o que gostaria de oferecer" />
                </S.Question>

                <S.Question>
                  <div className="q-label">
                    <span className="q-num">11.</span>
                    <span>Dados bancários para pagamento (a titularidade deve corresponder ao CNPJ informado)</span>
                  </div>
                  <S.Grid4>
                    <div>
                      <S.FieldLabel>Banco</S.FieldLabel>
                      <S.Input value={form.q11_banco} onChange={set("q11_banco")} />
                    </div>
                    <div>
                      <S.FieldLabel>Agência</S.FieldLabel>
                      <S.Input value={form.q11_agencia} onChange={set("q11_agencia")} />
                    </div>
                    <div>
                      <S.FieldLabel>Conta</S.FieldLabel>
                      <S.Input value={form.q11_conta} onChange={set("q11_conta")} />
                    </div>
                    <div>
                      <S.FieldLabel>Chave PIX</S.FieldLabel>
                      <S.Input value={form.q11_pix} onChange={set("q11_pix")} />
                    </div>
                  </S.Grid4>
                  <p className="q-note" style={{ marginTop: 10 }}>
                    Alterações de dados bancários só serão aceitas por canal oficial e
                    mediante confirmação, para prevenção de fraude.
                  </p>
                </S.Question>

                <S.LegalBox>
                  <div className="lb-title">Declaração do respondente</div>
                  <p>
                    Declaro, sob as penas da lei, que represento legalmente a empresa
                    acima identificada, com poderes de administração, e que as
                    respostas prestadas a vinculam para todos os fins de direito.
                  </p>
                </S.LegalBox>
              </S.Card>

              {/* ─── SEÇÃO 2 — REGIME ─── */}
              <S.Card>
                <S.SectionTitle>Seção 2 · Regime Tributário</S.SectionTitle>

                <S.Question>
                  <div className="q-label"><span className="q-num">12.</span><span>Qual o regime de tributação adotado pela empresa atualmente?</span></div>
                  <S.RadioGroup>
                    <Radio name="q12" value="lucro_real" checked={form.q12_regime === "lucro_real"} onChange={set("q12_regime")}>Lucro Real</Radio>
                    <Radio name="q12" value="lucro_presumido" checked={form.q12_regime === "lucro_presumido"} onChange={set("q12_regime")}>Lucro Presumido</Radio>
                    <Radio name="q12" value="simples" checked={form.q12_regime === "simples"} onChange={set("q12_regime")}>Simples Nacional</Radio>
                    <Radio name="q12" value="mei" checked={form.q12_regime === "mei"} onChange={set("q12_regime")}>MEI (Microempreendedor Individual)</Radio>
                    <Radio name="q12" value="outro" checked={form.q12_regime === "outro"} onChange={set("q12_regime")}>Outro</Radio>
                  </S.RadioGroup>
                  {form.q12_regime === "outro" && (
                    <S.InlineField>
                      <S.Input value={form.q12_outro} onChange={set("q12_outro")} placeholder="Descreva o regime" />
                    </S.InlineField>
                  )}
                </S.Question>

                <S.Question>
                  <div className="q-label"><span className="q-num">13.</span><span>Qual regime de apuração de IBS/CBS a empresa adotará a partir de 2027?</span></div>
                  <p className="q-note">Responder apenas se optante pelo Simples Nacional ou MEI.</p>
                  <S.RadioGroup>
                    <Radio name="q13" value="simples" checked={form.q13_regimeIbsCbs === "simples"} onChange={set("q13_regimeIbsCbs")}>
                      Permanência no Simples Nacional (apuração "por dentro" do DAS)
                    </Radio>
                    <Radio name="q13" value="hibrido" checked={form.q13_regimeIbsCbs === "hibrido"} onChange={set("q13_regimeIbsCbs")}>
                      Regime híbrido — apuração de IBS/CBS pelo regime regular, por fora do DAS (art. 41, §3º, LC 214/2025)
                    </Radio>
                    <Radio name="q13" value="migracao" checked={form.q13_regimeIbsCbs === "migracao"} onChange={set("q13_regimeIbsCbs")}>
                      Migração para Lucro Presumido ou Lucro Real
                    </Radio>
                  </S.RadioGroup>
                  {!isSimplesMei && form.q12_regime && (
                    <p className="q-note" style={{ marginTop: 8 }}>Não se aplica — regime selecionado na questão 12 não é Simples/MEI.</p>
                  )}
                </S.Question>

                <S.Question>
                  <div className="q-label"><span className="q-num">14.</span><span>Está ciente de que a opção pelo regime híbrido deve ser formalizada até 30/09/2026 (revisão até novembro/2026)?</span></div>
                  <p className="q-note">Responder apenas se optante pelo Simples Nacional ou MEI. A escolha impacta diretamente o crédito que a Automec poderá aproveitar.</p>
                  <S.RadioGroup>
                    <Radio name="q14" value="hibrido" checked={form.q14_cienteHibrido === "hibrido"} onChange={set("q14_cienteHibrido")}>
                      Sim — optaremos / já optamos pelo regime híbrido
                    </Radio>
                    <Radio name="q14" value="simples_tradicional" checked={form.q14_cienteHibrido === "simples_tradicional"} onChange={set("q14_cienteHibrido")}>
                      Sim — permaneceremos no Simples Nacional tradicional
                    </Radio>
                  </S.RadioGroup>
                </S.Question>

                <S.Question>
                  <div className="q-label"><span className="q-num">15.</span><span>A empresa possui regimes especiais ou benefícios fiscais vigentes (municipal, estadual ou federal)?</span></div>
                  <S.RadioGroup>
                    <Radio name="q15" value="nao" checked={form.q15_regimesEspeciais === "nao"} onChange={set("q15_regimesEspeciais")}>Não</Radio>
                    <Radio name="q15" value="sim" checked={form.q15_regimesEspeciais === "sim"} onChange={set("q15_regimesEspeciais")}>Sim</Radio>
                  </S.RadioGroup>
                  {form.q15_regimesEspeciais === "sim" && (
                    <S.InlineField>
                      <S.Input value={form.q15_descricao} onChange={set("q15_descricao")} placeholder="Descreva os regimes/benefícios" />
                    </S.InlineField>
                  )}
                </S.Question>

                <S.Question>
                  <div className="q-label"><span className="q-num">16.</span><span>A empresa mantém regularidade fiscal e certidões (CND) válidas, sem débitos que comprometam o recolhimento de tributos?</span></div>
                  <S.RadioGroup>
                    <Radio name="q16" value="sim" checked={form.q16_regularidade === "sim"} onChange={set("q16_regularidade")}>Sim</Radio>
                    <Radio name="q16" value="parcial" checked={form.q16_regularidade === "parcial"} onChange={set("q16_regularidade")}>Parcialmente</Radio>
                    <Radio name="q16" value="nao" checked={form.q16_regularidade === "nao"} onChange={set("q16_regularidade")}>Não</Radio>
                  </S.RadioGroup>
                  {["parcial", "nao"].includes(form.q16_regularidade) && (
                    <S.InlineField>
                      <S.Input value={form.q16_motivo} onChange={set("q16_motivo")} placeholder="Informe o motivo, casos em aberto e previsão de solução" />
                    </S.InlineField>
                  )}
                </S.Question>
              </S.Card>

              {/* ─── SEÇÃO 3 — NF ─── */}
              <S.Card>
                <S.SectionTitle>Seção 3 · Emissão de Notas Fiscais</S.SectionTitle>

                <S.Question>
                  <div className="q-label">
                    <span className="q-num">17.</span>
                    <span>Com a Reforma Tributária, todas as receitas passarão a ser passíveis de emissão de nota fiscal, de qualquer natureza. Sua empresa já está preparada para emitir nota fiscal de todas as suas receitas?</span>
                  </div>
                  <S.RadioGroup>
                    <Radio name="q17" value="sim" checked={form.q17_emiteNf === "sim"} onChange={set("q17_emiteNf")}>Sim</Radio>
                    <Radio name="q17" value="nao" checked={form.q17_emiteNf === "nao"} onChange={set("q17_emiteNf")}>Não</Radio>
                  </S.RadioGroup>
                  {form.q17_emiteNf === "nao" && (
                    <S.InlineField>
                      <S.Input value={form.q17_previsao} onChange={set("q17_previsao")} placeholder="Qual a previsão?" />
                    </S.InlineField>
                  )}
                </S.Question>

                <S.Question>
                  <div className="q-label">
                    <span className="q-num">18.</span>
                    <span>Qual o prazo legal para sua empresa se adaptar ao novo padrão de notas fiscais (com IBS/CBS destacados, quando for o caso, e demais exigências legais)?</span>
                  </div>
                  <S.TextArea value={form.q18_prazoAdaptacao} onChange={set("q18_prazoAdaptacao")} placeholder="Informe o prazo legal aplicável à sua empresa" />
                </S.Question>

                <S.LegalBox>
                  <div className="lb-title">Observação da Automec — Faz lei entre as partes</div>
                  <p>
                    A partir do prazo legal — em alguns casos dezembro/2026 e em
                    outros janeiro/2027 — a Automec não pagará quaisquer títulos sem
                    o recebimento da nota fiscal correspondente, conforme as
                    exigências legais (conforme o regime da empresa, contemplando o
                    IBS/CBS).
                  </p>
                </S.LegalBox>
              </S.Card>

              {/* ─── SEÇÃO 4 — FORNECIMENTO ─── */}
              <S.Card>
                <S.SectionTitle>Seção 4 · Fornecimento e Capacidade</S.SectionTitle>

                <S.Question>
                  <div className="q-label"><span className="q-num">19.</span><span>Existem fornecedores alternativos no mercado para o que sua empresa fornece à Automec?</span></div>
                  <S.RadioGroup>
                    <Radio name="q19" value="varios" checked={form.q19_fornecedoresAlternativos === "varios"} onChange={set("q19_fornecedoresAlternativos")}>Sim, há vários no mercado</Radio>
                    <Radio name="q19" value="poucos" checked={form.q19_fornecedoresAlternativos === "poucos"} onChange={set("q19_fornecedoresAlternativos")}>Sim, mas poucos</Radio>
                    <Radio name="q19" value="unico" checked={form.q19_fornecedoresAlternativos === "unico"} onChange={set("q19_fornecedoresAlternativos")}>Não — somos praticamente fornecedor único deste item/serviço</Radio>
                  </S.RadioGroup>
                </S.Question>

                <S.Question>
                  <div className="q-label"><span className="q-num">20.</span><span>Prazo médio de entrega ou atendimento e capacidade de fornecimento</span></div>
                  <S.FieldLabel>Prazo médio de entrega/atendimento</S.FieldLabel>
                  <S.Input value={form.q20_prazoEntrega} onChange={set("q20_prazoEntrega")} placeholder="Ex.: 15 dias úteis" />
                  <div style={{ marginTop: 14 }}>
                    <S.FieldLabel>Há capacidade para atender eventual aumento de demanda da Automec?</S.FieldLabel>
                    <S.RadioGroup>
                      <Radio name="q20" value="sim" checked={form.q20_capacidadeDemanda === "sim"} onChange={set("q20_capacidadeDemanda")}>Sim</Radio>
                      <Radio name="q20" value="parcial" checked={form.q20_capacidadeDemanda === "parcial"} onChange={set("q20_capacidadeDemanda")}>Parcialmente</Radio>
                      <Radio name="q20" value="nao" checked={form.q20_capacidadeDemanda === "nao"} onChange={set("q20_capacidadeDemanda")}>Não</Radio>
                    </S.RadioGroup>
                  </div>
                </S.Question>
              </S.Card>

              {/* ─── SEÇÃO 5 — CONDIÇÕES ─── */}
              <S.Card>
                <S.SectionTitle>Seção 5 · Preços, Recolhimento e Obrigações</S.SectionTitle>

                <S.LegalBox>
                  <div className="lb-title">Observação da Automec — Faz lei entre as partes</div>
                  <p>A Automec não aceitará reajustes de preços sem a devida comprovação de majoração de custos.</p>
                </S.LegalBox>

                <S.Question>
                  <div className="q-label"><span className="q-num">21.</span><span>Ao assinar este documento, a empresa declara concordar com as seguintes condições:</span></div>
                  <S.TermsBox>
                    <p><strong>a)</strong> A empresa concorda que, enquanto fornecedora da Automec e caso seja contribuinte do CBS/IBS, recolherá tais tributos na forma da lei. Caso, até a data do pagamento de cada compra, o recolhimento não seja comprovado, a Automec poderá reter e/ou descontar do valor a pagar o montante correspondente a tais tributos e recolhê-los, a fim de assegurar o seu direito ao crédito sobre tais compras, mediante posterior prestação de contas ao fornecedor.</p>
                    <p><strong>b)</strong> A empresa concorda em seguir as instruções da Automec para pagamento das notas fiscais e em fornecer os documentos e comprovantes solicitados no prazo de 5 (cinco) dias úteis, para verificação de sua situação fiscal.</p>
                    <p><strong>c)</strong> A empresa está ciente de que: os DANFEs deverão ser enviados ao e-mail {config.emailFiscal}; as notas fiscais deverão conter, no campo de observações, o número do pedido da Automec (quando houver) e o nome do comprador da Automec; as mercadorias deverão ser entregues na sede da Automec indicada no pedido, e nunca em outro local, mediante protocolo de entrega; e, tratando-se de serviço, deverá ser colhido o "de acordo" do comprador da Automec ao final da execução.</p>
                    <p><strong>d)</strong> A empresa declara conhecer e cumprir a legislação anticorrupção (Lei nº 12.846/2013) e a legislação trabalhista aplicável, não se utilizando de trabalho infantil, forçado ou análogo ao de escravo, e compromete-se a atuar com integridade em suas relações com a Automec.</p>
                  </S.TermsBox>
                  <div style={{ marginTop: 12 }}>
                    <S.RadioOption $checked={form.q21_aceite} onClick={() => set("q21_aceite")(!form.q21_aceite)}>
                      <span className="box">{form.q21_aceite ? "X" : ""}</span>
                      <span>Li e concordo, sem ressalvas, com todas as condições acima.</span>
                    </S.RadioOption>
                  </div>
                </S.Question>
              </S.Card>

              {/* ─── SEÇÃO 6 — OBS ─── */}
              <S.Card>
                <S.SectionTitle>Seção 6 · Observações</S.SectionTitle>
                <S.Question>
                  <div className="q-label"><span className="q-num">22.</span><span>Espaço livre para observações, dúvidas ou sugestões</span></div>
                  <S.TextArea value={form.q22_observacoes} onChange={set("q22_observacoes")} placeholder="Opcional" />
                </S.Question>
              </S.Card>
            </>
          )}

          {/* ─── SEÇÃO VII — ASSINATURA ─── */}
          <S.Card>
            <S.SectionTitle>Seção VII · Declaração Final e Assinatura</S.SectionTitle>

            <p style={{ fontSize: "0.88em", lineHeight: 1.65, color: "#374151", marginTop: 14 }}>
              Declaro, sob as penas da lei, que as informações prestadas são
              verdadeiras e completas; que possuo poderes para representar
              legalmente a empresa; e que as respostas e obrigações aqui assumidas a
              vinculam para todos os fins de direito, fazendo lei entre as partes.
            </p>
            <p style={{ fontSize: "0.8em", color: "#64748b", fontStyle: "italic", marginTop: 10 }}>
              A assinatura eletrônica abaixo tem validade jurídica e é aceita pela Automec.
            </p>

            <S.Question>
              <div className="q-label"><span>Local e data</span></div>
              <S.Input value={form.localData} onChange={set("localData")} placeholder={`Ex.: Sorocaba/SP, ${new Date().toLocaleDateString("pt-BR")}`} />
            </S.Question>

            <S.Question>
              <div className="q-label"><span>Assinatura do representante legal</span></div>
              <SignaturePad ref={sigRef} onChange={setHasSignature} />
            </S.Question>

            <S.Grid2>
              <S.Question>
                <div className="q-label"><span>Nome</span></div>
                <S.Input value={form.assinaturaNome} onChange={set("assinaturaNome")} placeholder="Nome completo" />
              </S.Question>
              <S.Question>
                <div className="q-label"><span>CPF</span></div>
                <S.Input value={form.assinaturaCpf} onChange={(e) => set("assinaturaCpf")(maskCpf(e.target.value))} placeholder="000.000.000-00" inputMode="numeric" />
              </S.Question>
            </S.Grid2>

            <S.Question>
              <div className="q-label"><span>Cargo</span></div>
              <S.Input value={form.assinaturaCargo} onChange={set("assinaturaCargo")} placeholder="Cargo do representante legal" />
            </S.Question>

            <S.LegalBox>
              <div className="lb-title">Proteção de dados (LGPD)</div>
              <p>
                Os dados pessoais coletados neste documento serão tratados
                exclusivamente para fins de cadastro, homologação e gestão do
                fornecimento, em conformidade com a Lei nº 13.709/2018 (LGPD).
              </p>
            </S.LegalBox>
          </S.Card>

          <S.SubmitBar>
            {errors.length > 0 && (
              <S.ErrorList>
                <div className="title"><FiAlertTriangle /> Corrija os itens abaixo antes de enviar</div>
                <ul>
                  {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </S.ErrorList>
            )}
            <div style={{ marginTop: 20 }}>
              <S.SubmitButton type="submit" disabled={submitting}>
                {submitting ? "Enviando..." : <><FiSend /> Enviar declaração assinada</>}
              </S.SubmitButton>
            </div>
          </S.SubmitBar>

          <S.Footer>
            <FiFileText style={{ verticalAlign: -2 }} /> Grupo Automec · Departamento de Compras · Documento válido para todos os fins de direito
          </S.Footer>
        </form>
      </S.Container>
    </S.Page>
  );
}
